import { contextualError } from '@/shared/utils/error';

// frontend/shared/services/cartApi.ts
export class CartApiService {
  private static getLocalizedPaths = () => ({
    CART: window.Shopify.routes.root + 'cart.js',
    ADD: window.Shopify.routes.root + 'cart/add.js',
    CHANGE: window.Shopify.routes.root + 'cart/change.js',
    UPDATE: window.Shopify.routes.root + 'cart/update.js'
  });

  private static cartAPI = {
    get: (url: string) =>
      fetch(url, {
        cache: 'no-store'
      }),
    post: (
      url: string,
      payload:
        | ShopifyCartAddPayload
        | ShopifyCartChangePayload
        | ShopifyCartUpdatePayload
    ) =>
      fetch(url, {
        cache: 'no-store',
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
  };

  static addToCart = async (payload: ShopifyCartAddPayload) => {
    const localizedPaths = this.getLocalizedPaths();
    const response = await this.cartAPI.post(localizedPaths.ADD, payload);

    if (response.ok) {
      return await this.getCart();
    }

    const error = await extractErrorFromResponse(
      response as ShopifyCartErrorResponse
    );

    // Shopify response with stock-related errors
    if (response.status === HTTP_RESPONSE_TYPE.SHOPIFY_STOCK_ERROR) {
      await this.getCart();

      throw error;
    }

    throw error;
  };

  static async changeCartQty(payload: ShopifyCartChangePayload) {
    const localizedPaths = this.getLocalizedPaths();
    const response = await this.cartAPI.post(localizedPaths.CHANGE, payload);

    if (response.ok) {
      return { cart: await response.json() };
    } else {
      const error = await extractErrorFromResponse(
        response as ShopifyCartErrorResponse
      );

      await this.getCart();

      return { error };
    }
  }

  /**
   * Update multiple line item quantities in one request (POST cart/update.js with `updates`).
   * Keys must be line item `key` or variant IDs; values are the new quantities.
   * Use line item keys when the same variant appears in multiple lines (e.g. gift with _pickedReward).
   */
  static async updateCartQuantities(updates: Record<string, number>) {
    const localizedPaths = this.getLocalizedPaths();
    const response = await this.cartAPI.post(localizedPaths.UPDATE, {
      updates
    });

    if (response.ok) {
      return { cart: await response.json() };
    } else {
      const error = await extractErrorFromResponse(
        response as ShopifyCartErrorResponse
      );
      await this.getCart();
      return { error };
    }
  }

  static async getCart() {
    const paths = this.getLocalizedPaths();

    try {
      const response = await this.cartAPI.get(paths.CART);
      return await response.json();
    } catch (error) {
      console.error(`Failed`, error);
    }
  }

  static updateCartAttribute = async (attribute: string, value: string) => {
    const localizedPaths = this.getLocalizedPaths();
    const response = await this.cartAPI.post(localizedPaths.UPDATE, {
      attributes: {
        [attribute]: value
      }
    });

    if (!response.ok) {
      const error = await extractErrorFromResponse(
        response as ShopifyCartErrorResponse
      );
      throw contextualError('UpdateCartAttributeError', error, {
        message: `Unable to set cart attribute: ${attribute}=${value}`
      });
    }

    return { success: true };
  };

  /**
   * Removes a cart attribute (sets to empty string). Returns the updated cart from the response
   * so callers can avoid an extra getCart().
   */
  static removeCartAttribute = async (
    attribute: string
  ): Promise<ShopifyCart | undefined> => {
    const localizedPaths = this.getLocalizedPaths();
    const response = await this.cartAPI.post(localizedPaths.UPDATE, {
      attributes: {
        [attribute]: ''
      }
    });

    if (!response.ok) {
      const error = await extractErrorFromResponse(
        response as ShopifyCartErrorResponse
      );
      throw contextualError('RemoveCartAttributeError', error, {
        message: `Unable to remove cart attribute: ${attribute}`
      });
    }

    const cart: ShopifyCart = await response.json();
    return cart;
  };
}

export const cartAttributes = {
  ENABLE_CROSS_SELL: 'enable_others_bought'
};

const HTTP_RESPONSE_TYPE = {
  SHOPIFY_STOCK_ERROR: 422,
  TOO_MANY_REQUESTS: 429,
  SERVICE_UNAVAILABLE: 503
};

const extractErrorFromResponse = async (response: ShopifyCartErrorResponse) => {
  // Shopify response with stock-related errors
  if (response.status === HTTP_RESPONSE_TYPE.SHOPIFY_STOCK_ERROR) {
    const error = await response.json();
    return new Error(error.description ?? error.message);
  }

  if (response.status === HTTP_RESPONSE_TYPE.TOO_MANY_REQUESTS) {
    return new Error(window.Hobbii?.errorMessages.tooManyRequests);
  }

  if (response.status === HTTP_RESPONSE_TYPE.SERVICE_UNAVAILABLE) {
    return new Error(window.Hobbii?.errorMessages.serviceUnavailable);
  }

  return new Error(window.Hobbii?.errorMessages.unknownErrorOccurred);
};
